import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminKpiGrid } from '@/components/admin/AdminKpiGrid';
import { ConversionChart, EngagementChart, QualityChart, ScenarioChart } from '@/components/admin/AdminOverviewCharts';
import { AdminConversationTable } from '@/components/admin/AdminConversationTable';
import { TranscriptDialog } from '@/components/admin/TranscriptDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOverview() {
  const { timeParams } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transcriptId, setTranscriptId] = useState(null);

  const load = useCallback(async () => {
    if (timeParams.range === 'custom' && (!timeParams.start_date || !timeParams.end_date)) { setLoading(false); return; }
    setLoading(true); setError('');
    try { const response = await axios.get(`${API}/admin/overview`, { params: timeParams, withCredentials: true }); setData(response.data); }
    catch (requestError) { setData(null); setError(requestError.response?.data?.detail || 'Overview could not be loaded.'); }
    finally { setLoading(false); }
  }, [timeParams]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="space-y-4" data-testid="admin-overview-loading"><div className="h-40 animate-pulse bg-[#EEE9E1]" /><div className="h-80 animate-pulse bg-[#EEE9E1]" /></div>;
  if (error) return <div className="border-l-4 border-[#D26046] bg-[#FBEDE8] p-5 text-sm text-[#8A3B27]" data-testid="admin-overview-error">{error}</div>;
  if (!data) return <div className="border border-dashed border-[#CFC8BD] p-12 text-center text-sm text-[#8A8D8A]" data-testid="admin-custom-date-empty">Choose both custom dates to load the dashboard.</div>;

  return (
    <div className="space-y-8" data-testid="admin-overview-page">
      <div className="flex items-center justify-between"><p className="text-xs text-[#8A8D8A]">Updated {new Date(data.generated_at).toLocaleTimeString()}</p><button onClick={load} className="p-2 text-[#6B6E69] hover:text-[#2C5545]" aria-label="Refresh overview" data-testid="admin-overview-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button></div>
      <AdminKpiGrid kpis={data.kpis} />
      <div className="grid gap-5 lg:grid-cols-4"><EngagementChart data={data.engagement_series} /><ConversionChart data={data.conversion_funnel} /></div>
      <div className="grid gap-5 lg:grid-cols-3"><ScenarioChart data={data.scenario_performance} /><QualityChart data={data.scenario_performance} /></div>
      <section className="space-y-4"><div><p className="text-[10px] font-semibold uppercase text-[#D26046]">Latest activity</p><h2 className="mt-2 font-serif text-2xl">Recent conversations</h2></div><AdminConversationTable items={data.recent_conversations} onView={setTranscriptId} compact /></section>
      <TranscriptDialog sessionId={transcriptId} open={Boolean(transcriptId)} onOpenChange={(open) => !open && setTranscriptId(null)} />
    </div>
  );
}
